/**
 * Normalizes call_server.py batch status for the Customers dashboard.
 * @see voice-marketing/call_server.py BatchCallManager.get_status
 */

export const EMPTY_BATCH_STATUS = {
  running: false,
  inFlight: false,
  idle: true,
  queued: [],
  active: [],
  done: [],
  failed: [],
  total: 0,
  states: {},
};

const toIdList = (arr) => (arr || []).map((id) => Number(id)).filter((n) => !Number.isNaN(n));

export function normalizeBatchStatus(raw) {
  if (!raw) return { ...EMPTY_BATCH_STATUS };

  let queued = toIdList(raw.queued);
  let active = toIdList(raw.active);
  let done = toIdList(raw.done);
  let failed = toIdList(raw.failed);

  const states = raw.states && typeof raw.states === 'object' ? raw.states : {};
  const byState = { active: [], queued: [], done: [], failed: [] };

  Object.entries(states).forEach(([key, info]) => {
    const id = Number(key);
    if (Number.isNaN(id)) return;
    const state = (info?.state || '').toLowerCase();
    if (state === 'active') byState.active.push(id);
    else if (state === 'queued') byState.queued.push(id);
    else if (state === 'done') byState.done.push(id);
    else if (state === 'failed') byState.failed.push(id);
  });

  const mergeUnique = (a, b) => [...new Set([...a, ...b])];
  active = mergeUnique(active, byState.active);
  queued = mergeUnique(queued, byState.queued);
  done = mergeUnique(done, byState.done);
  failed = mergeUnique(failed, byState.failed);

  const inFlight = typeof raw.in_flight === 'boolean'
    ? raw.in_flight
    : (!!raw.running || active.length > 0 || queued.length > 0);

  const idle = typeof raw.idle === 'boolean' ? raw.idle : !inFlight;

  return {
    running: !!raw.running,
    inFlight,
    idle,
    batchComplete: !!raw.batch_complete,
    queued,
    active,
    done,
    failed,
    total: Number(raw.total || 0),
    states,
  };
}

/** Show Stop button + progress bar while any call is ringing or in progress. */
export function batchHasActivity(status) {
  if (!status) return false;
  if (status.active.length > 0 || status.queued.length > 0) return true;
  if (status.running) return true;
  return false;
}

export function getCustomerBatchState(status, customerId) {
  const id = Number(customerId);
  const entry = status?.states?.[String(id)] ?? status?.states?.[id];
  if (entry?.state) return entry.state;
  if (status?.active?.includes(id)) return 'active';
  if (status?.queued?.includes(id)) return 'queued';
  if (status?.done?.includes(id)) return 'done';
  if (status?.failed?.includes(id)) return 'failed';
  return null;
}

export function normalizePendingCallCounts(callCounts, status) {
  if (status?.idle && !status?.running) return {};
  if (!callCounts) return {};
  return Object.fromEntries(
    Object.entries(callCounts).map(([id, count]) => [Number(id), Number(count)]),
  );
}

/** CRM row should use follow-up agent when they have voice notes or operator remarks. */
export function customerNeedsFollowUpCall(customer) {
  if (!customer) return false;
  const summary = (customer.call_summary || '').trim();
  const remarks = (customer.remarks || '').trim();
  return summary.length > 0 || remarks.length > 0;
}
